import { formatToE164 } from './twilio';
import { sendSMSWithTestCredentials } from './twilio';

interface SendSMSParams {
  businessId: string;
  to: string;
  message: string;
}

interface SendSMSResult {
  success: boolean;
  error?: string;
}

/**
 * Sends an SMS message using Twilio
 */
export async function sendSMS({ businessId, to, message }: SendSMSParams): Promise<SendSMSResult> {
  try {
    console.log(`[SMS] 🚀 Sending SMS: to=${to}, messageLength=${message.length}, businessId=${businessId}`);
    
    // Use the existing Twilio functions that we know work (from the settings page)
    const result = await sendSMSWithTestCredentials(businessId, to, message);
    
    if (result.success) {
      console.log(`[SMS] ✅ SMS sent successfully`);
    } else {
      console.error(`[SMS] ❌ Failed to send SMS:`, result.error);
    }
    
    return result;
  } catch (error) {
    console.error('[SMS] ❌ Error in sendSMS:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send SMS'
    };
  }
}

/**
 * Sends a notification SMS to a customer
 */
export async function sendCustomerNotification(
  businessId: string,
  customerPhone: string,
  template: 'booking_confirmation' | 'booking_reminder' | 'booking_cancelled',
  data: Record<string, string>
): Promise<SendSMSResult> {
  const templates = {
    booking_confirmation: `Your booking {booking_number} has been confirmed for {date} at {time}. We look forward to seeing you!`,
    booking_reminder: `Reminder: Your booking {booking_number} is scheduled for tomorrow at {time}. See you then!`,
    booking_cancelled: `Your booking {booking_number} has been cancelled. If you have any questions, please contact us.`
  };

  let message = templates[template];
  
  // Replace placeholders with actual data
  Object.entries(data).forEach(([key, value]) => {
    message = message.replace(`{${key}}`, value);
  });

  return sendSMS({
    businessId,
    to: customerPhone,
    message
  });
}

/**
 * Sends a custom SMS message to a customer
 */
export async function sendCustomMessage(
  businessId: string,
  customerPhone: string,
  message: string
): Promise<SendSMSResult> {
  console.log(`[CUSTOM SMS] 🚀 Sending custom message to ${customerPhone}`);
  
  if (!businessId) {
    console.error('[CUSTOM SMS] ❌ Missing business ID');
    return { success: false, error: 'Missing business ID' };
  }
  
  if (!customerPhone) {
    console.error('[CUSTOM SMS] ❌ Missing customer phone number');
    return { success: false, error: 'Missing customer phone number' };
  }
  
  if (!message || message.trim() === '') {
    console.error('[CUSTOM SMS] ❌ Empty message');
    return { success: false, error: 'Empty message' };
  }
  
  const result = await sendSMS({
    businessId,
    to: customerPhone,
    message
  });
  
  if (result.success) {
    console.log(`[CUSTOM SMS] ✅ Message sent successfully to ${customerPhone}`);
  } else {
    console.error(`[CUSTOM SMS] ❌ Failed to send to ${customerPhone}:`, result.error);
  }
  
  return result;
} 